import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./fba-create-page.module.css";
import TextInput from "@/components/forms/TextInput/TextInput";
import Select from "@/components/forms/Select/Select";
import Button from "@/components/forms/Button/Button";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Switch from "@/components/forms/Switch/Switch";
import Accordion from "@/components/Accordion/Accordion";
import IconPlus from "@/components/icons/IconPlus";
import { AutoFillListPriceFrom, AutoFillListPriceOperation, BoxContentsProvider, CreateInboundBatchBody, FulfillmentType, LabelPrepPreference, PackingType, TransportType } from "@boxem/core-api-schema";
import { useUserContext, Address } from "@/contexts/UserContext";
import { useLocation } from "wouter";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { createBatchV2 } from "@/api/fulfillment";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import dayjs from "dayjs";
import { LocalStorageKeys, getLocalStorageItem, setLocalStorageItem } from "@/utils/localStorageUtils";
import ChangeSubscriptionModal, { ChangeSubscriptionModalMode } from "@/components/ChangeSubscriptionModal/ChangeSubscriptionModal";
import AddressModal, { StateOptions } from "@/components/AddressModal/AddressModal";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import First2DBarcodeShipmentModal from "@/components/First2DBarcodeShipmentModal/First2DBarcodeShipmentModal";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import Alert from "@/components/Alert/Alert";
import { SessionStorageKeys } from "@/utils/sessionStorageUtils";
import AddressDropdown from "@/components/AddressDropdown/AddressDropdown";
import { FormField, useForm } from "@/components/Form";

type FbaCreatePageProps = {};

export const CreateShipmentFormDefault: CreateInboundBatchBody = {
  name: "",
  fromAddressId: 0,
  fulfillmentType: FulfillmentType.FBA,
  shipmentType: TransportType.SPD,
  packingType: PackingType.INDIVIDUAL,
  labelPrepPreference: LabelPrepPreference.SELLER_LABEL,
  boxContentsProvider: BoxContentsProvider.Boxem,
  printBoxLabelsStep3: false,

  autoFillListPrice: false,
  autoFillListPriceFrom: AutoFillListPriceFrom.BUY_BOX,
  autoFillListPriceOperation: AutoFillListPriceOperation.INCREASE, 
  autoFillListPriceAmount: "25",

  showSupplierInput: true,
  showBuyCostInput: false,
  showDatePurchasedInput: false,

  printFNSKUAfterAddingToShipment: false,
  printFNSKUAfterIncreaseQuantityInShipment: false,
  printFNSKUAfterAssigningToBox: false,
  printFNSKUAfterIncreaseQuantityInBox: false,
};

const FbaCreatePage = ({}: FbaCreatePageProps) => {
  const { user, addressBook } = useUserContext();
  const [location, setLocation] = useLocation();
  const { subscription } = useSubscriptionContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const createShipmentFormState = useForm(CreateShipmentFormDefault);
  const { value: createShipmentForm, setValue: setCreateShipmentForm, state: formFields } = createShipmentFormState;
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [moveToStep1ButtonDisabled, setMoveToStep1ButtonDisabled] = useState<boolean>(true);
  const [loadingMoveToStep1, setLoadingMoveToStep1] = useState<boolean>(false);

  const [shippingMethodStates, setShippingMethodStates] = useState([true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  const [boxContentStates, setBoxCententStates] = useState([true, true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  const [changeSubscriptionModalMode, setChangeSubscriptionModalMode] = useState(ChangeSubscriptionModalMode.closed);
  const [first2DBarcodeShipmentModalOpen, setFirst2DBarcodeShipmentModalOpen] = useState<boolean>(false);
  const [ltlWarningModalOpen, setLTLWarningModalOpen] = useState<boolean>(false);
  const [nonSupportedCarrierModalOpen, setNonSupportedCarrierModalOpen] = useState<boolean>(false);

  // @TODO change to annual/monthly plan according to current subscription
  const [newPlanCode, setNewPlanCode] = useState('professional-launch-monthly-inapp');

  const show2DBarcodeModal = !getLocalStorageItem("boxem-DS2DBC", false) && createShipmentForm.boxContentsProvider === BoxContentsProvider.Barcode2D;
  const showLTLWarningModal = !getLocalStorageItem(LocalStorageKeys.ltlCancelWarningCreatePage);
  const showNonContiguous48StatesWarningModal = !window.sessionStorage.getItem(SessionStorageKeys.doneNonContiguous48StatesWarningModal);

  const getAddressValue = (): string => {
    const address = addressBook.find(address => address.id === createShipmentForm.fromAddressId);
    let addressValue = "";

    if (!address?.address1) return addressValue;

    if (!!address?.address1) addressValue = addressValue + `${address?.address1}, `;
    if (!!address?.address2) addressValue = addressValue + `${address?.address2}, `;
    if (!!address?.city)     addressValue = addressValue + `${address?.city} `;
    if (!!address?.state)    addressValue = addressValue + `${address?.state} `;
    if (!!address?.zipcode)  addressValue = addressValue + `${address?.zipcode}`;

    return addressValue;
  };

  const handleFirst2DBarcodeShipmentModalClose = () => {
    setFirst2DBarcodeShipmentModalOpen(false);
    handleCreateShipment();
  };

  const handleCreateShipment = () => {
    setLoadingMoveToStep1(true);
    createBatchV2(createShipmentForm)
      .then(batch => {
        setLocation(`/fba/shipments/${batch.id}?return=${encodeURIComponent(location)}`);
        setCreateShipmentForm({...CreateShipmentFormDefault});
      })
      .catch(errors => {
        if (handleErrorResponseGenerically(errors)) {
          return;
        }
      })
      .finally(() => {
        setLoadingMoveToStep1(false);
      });
  };

  const handleFulfillmentTypeChange = (e: FulfillmentType) => {
    if (e === FulfillmentType.FBM) setLocation("/fbm/listings/create");
  };

  useEffect(() => {
    setShippingMethodStates([true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
    setBoxCententStates([true, true, (subscription && subscription.planCode.indexOf('professional') >= 0)]);
  }, [subscription]);

  useEffect(() => {
    setCreateShipmentForm({
      ...createShipmentForm,
      shipmentType: user?.settings.shippingMethod ?? CreateShipmentFormDefault.shipmentType,
      fromAddressId: createShipmentForm.fromAddressId ? createShipmentForm.fromAddressId : (user?.settings.fromId ?? CreateShipmentFormDefault.fromAddressId),
      packingType: user?.settings.packingType ?? CreateShipmentFormDefault.packingType,
      labelPrepPreference: user?.settings.labelingPreference ?? CreateShipmentFormDefault.labelPrepPreference,
      boxContentsProvider: user?.settings.boxContentsPreference ?? CreateShipmentFormDefault.boxContentsProvider,
      printBoxLabelsStep3: user?.settings.printLabelsEarly !== undefined ? user?.settings.printLabelsEarly : CreateShipmentFormDefault.printBoxLabelsStep3,

      autoFillListPrice: user?.settings.autoFillListPrice !== undefined ? user?.settings.autoFillListPrice : CreateShipmentFormDefault.autoFillListPrice,
      autoFillListPriceFrom: user?.settings.autoFillListPriceFrom ?? CreateShipmentFormDefault.autoFillListPriceFrom,
      autoFillListPriceOperation: user?.settings.autoFillListPriceOperation ?? CreateShipmentFormDefault.autoFillListPriceOperation,
      autoFillListPriceAmount: user?.settings.autoFillListPriceAmount ?? CreateShipmentFormDefault.autoFillListPriceAmount,

      showBuyCostInput: true,
      showSupplierInput: false,
      showDatePurchasedInput: false,

      printFNSKUAfterAddingToShipment: user?.settings.printFNSKUAfterAddingToShipment !== undefined ? user?.settings.printFNSKUAfterAddingToShipment : CreateShipmentFormDefault.printFNSKUAfterAddingToShipment,
      printFNSKUAfterIncreaseQuantityInShipment: user?.settings.printFNSKUAfterIncreaseQuantityInShipment !== undefined ? user?.settings.printFNSKUAfterIncreaseQuantityInShipment : CreateShipmentFormDefault.printFNSKUAfterIncreaseQuantityInShipment,
      printFNSKUAfterAssigningToBox: user?.settings.printFNSKUAfterAssigningToBox !== undefined ? user?.settings.printFNSKUAfterAssigningToBox : CreateShipmentFormDefault.printFNSKUAfterAssigningToBox,
      printFNSKUAfterIncreaseQuantityInBox: user?.settings.printFNSKUAfterIncreaseQuantityInBox !== undefined ? user?.settings.printFNSKUAfterIncreaseQuantityInBox : CreateShipmentFormDefault.printFNSKUAfterIncreaseQuantityInBox,
    
      readyToShip: dayjs().add(1, "day").toISOString(), // default value is tomorrow
    });
  }, [user]);

  const validateAddress = useCallback((fromAddressId: number) => {
    if (fromAddressId === 0) {
      return "Address is required";
    } else {
      const address = addressBook.find(a => a.id === fromAddressId);
      if (address && !address.phone) {
        return "Address is missing a phone number - click 'Edit Address' to resolve.";
      }
    }
  }, [addressBook]);

  useEffect(() => {
    setMoveToStep1ButtonDisabled(createShipmentFormState.validate({ quiet: true }) === false);
  }, [createShipmentForm]);

  return (
    <div className={styles.createPageContainer}>
      <First2DBarcodeShipmentModal
        open={first2DBarcodeShipmentModalOpen}
        onClose={() => setFirst2DBarcodeShipmentModalOpen(false)}
        onConfirm={handleFirst2DBarcodeShipmentModalClose}
      />

      <AddressModal
        open={addressModalOpen}
        editingAddress={addressBook.find(a => a.id === createShipmentForm.fromAddressId)}
        onClose={(newAddress) => {
          if (newAddress && newAddress.id) {
            setCreateShipmentForm({...createShipmentForm, fromAddressId: newAddress.id }, true);
          }
          setAddressModalOpen(false);
        }}
      />

      <ChangeSubscriptionModal
        targetPlanCode={newPlanCode}
        mode={changeSubscriptionModalMode}
        setMode={setChangeSubscriptionModalMode}
        onClose={() => {
          setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.closed);
        }}
      />

      <ConfirmModal
        open={ltlWarningModalOpen}
        body={
          <Alert
            type="warning"
            message={
              <p>
                Due to an ongoing issue with the Amazon Selling-Partner API, LTL shipment cancellations may fail. Cancellation must be done via Seller Central. 
                By proceeding, you are accepting responsibility for manually cancelling your LTL shipments on Seller Central, if you wish to
                cancel. <a href="https://sellercentral.amazon.com/gp/ssof/shipping-queue.html#fbashipment" target="_blank">View Shipments on Seller Central</a>
              </p>
            }
          />
        }
        onClose={confirmed => {
          setLTLWarningModalOpen(false);
          if (confirmed) {
            handleCreateShipment();
            setLocalStorageItem(LocalStorageKeys.ltlCancelWarningCreatePage, true)
          }
        }}
      />

      <ConfirmModal
        open={nonSupportedCarrierModalOpen}
        onClose={confirmed => {
          if (confirmed) {
            window.sessionStorage.setItem(SessionStorageKeys.doneNonContiguous48StatesWarningModal, SessionStorageKeys.doneNonContiguous48StatesWarningModal);
            handleCreateShipment();
          }
          setNonSupportedCarrierModalOpen(false);
        }}
        title={"No Carriers Supported for Source Address"}
        body={
          <Flex direction="column" gap={2}>
            <p>
              Amazon-partnered carriers for shipments are only available for the 48 contiguous United States - this excludes Hawaii and Alaska. Because your source address doesn't look like it's from one of the 48 contiguous states, you may be unable to complete your shipment in Boxem if you proceed. Sorry for the inconvienence. We recommend Seller Central if you encounter issues during this process. We are working to improve this in the future.
            </p>
            <a href="https://sellercentral.amazon.com/gp/ssof/shipping-queue.html#fbashipment">Shipping Queue on Seller Central</a>
          </Flex>
        }
        confirmButtonLabel="Ok!"
      />


      <div className={styles.root}>
        <div className={`${styles.header} strong`}>Create New Shipment</div>

        <div className={styles.content}>
          <div className={styles.col}>
            <div className={styles.section}>
              <div className={`${styles.sectionTitle} strong`}>Details</div>
              <FormField className={styles.rowItemA} form={createShipmentFormState} name="name" displayName="Name">
                <div className={styles.rowItemALabel}>Shipment Name:</div>
                <div className={styles.rowItemAContent}>
                    <TextInput
                      placeholder="Name your shipment"
                      maxLength={40}
                      value={createShipmentForm.name}
                      onChange={e => setCreateShipmentForm({...createShipmentForm, name: e })}  
                      invalid={formFields.name.invalid}
                    />
                </div>
              </FormField>
              <div className={styles.rowItemA}>
                <div className={styles.rowItemALabel}>Fulfillment Type:</div>
                <div className={styles.rowItemAContent}>
                  <Select
                    placeholder="Fulfillment type"
                    fullWidth
                    options={[FulfillmentType.FBA, FulfillmentType.FBM]}
                    defaultOption={FulfillmentType.FBA}
                    optionRender={option => {
                      if (option === FulfillmentType.FBA) {
                        return "FBA (Fulfillment By Amazon)"
                      } else if (option === FulfillmentType.FBM) {
                        return "FBM (Fulfillment By Merchant)"
                      }
                    }}
                    onChange={handleFulfillmentTypeChange}
                  />
                </div>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.section}>
              <div className={`${styles.sectionTitle} strong`}>Shipping & Origin</div>
              <FormField
                form={createShipmentFormState}
                name="fromAddressId"
                validator={validateAddress}
                alignInvalid="left"
                className={`${styles.rowItemA} ${styles.rowItemShipFrom}`}
                style={addressBook.length === 0 ? {"justifyContent":"start"} : null}
              >
                <div>
                  <div className={`${styles.rowItemA} ${styles.rowItemShipFrom}`}>
                      <div className={`${styles.rowItemALabel} ${styles.rowItemLabelShipForm}`}>Ship From:</div>
                      {addressBook.length > 0 && (
                        <div className={styles.rowItemAContent}>
                            <AddressDropdown
                              addressBook={addressBook}
                              disabled={loadingMoveToStep1}
                              invalid={formFields.fromAddressId.invalid}
                              value={createShipmentForm.fromAddressId}
                              onChange={id => setCreateShipmentForm({...createShipmentForm, fromAddressId: id })}
                            />
                        </div>
                      )}
                      <Button
                        type="link"
                        onClick={() => {
                          setAddressModalOpen(true);
                        }}
                        >
                        <span style={{minWidth:"max-content"}}>{addressBook.length > 0 ? "Edit Address" : "Create New Address"}</span>
                      </Button>
                  </div>
                  {createShipmentForm.fromAddressId !== 0 && (
                    <p className={styles.rowItemAContentSubText}>{getAddressValue()}</p>
                  )}
                </div>
              </FormField>
              <div className={styles.rowItemA}>
                <div className={styles.rowItemALabel}>Shipping Method:</div>
                <div className={styles.rowItemAContent}>
                  <Select
                    options={[TransportType.SPD, TransportType.LTL]}
                    optionStates={shippingMethodStates}
                    defaultOption={TransportType.SPD}
                    optionRender={(option) => {
                      switch (option) {
                        case TransportType.SPD:
                          return "SPD (Small Parcel Delivery)";

                        case TransportType.LTL:
                          return (
                            <div className={styles.ltlOption}>
                              LTL (Less than Truck Load){(subscription && subscription.planCode.indexOf('professional') < 0) ? (
                                <Button
                                  type="primary"
                                  className={styles.upgradeButton}
                                  onClick={(e) => {
                                    e.stopPropagation(true);
                                    setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.update);
                                  }}
                                >
                                  Upgrade Plan
                                </Button>
                              ) : (
                                <></>
                              )}
                            </div>
                          );
                      }
                    }}
                    selectionRender={option => {
                      switch (option) {
                        case TransportType.SPD:
                          return "SPD (Small Parcel Delivery)";
                        case TransportType.LTL:
                          return "LTL (Less than Truck Load)";
                      }
                    }}
                    placeholder="SPD (Small Parcel Delivery)"
                    fullWidth
                    value={createShipmentForm.shipmentType}
                    onChange={e => {
                      if (e === TransportType.LTL) {
                        setCreateShipmentForm({...createShipmentForm, shipmentType: e, boxContentsProvider: BoxContentsProvider.Barcode2D});
                      } else {
                        setCreateShipmentForm({...createShipmentForm, shipmentType: e });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.divider}></div>
            <div className={styles.section}>
              <div className={`${styles.sectionTitle} strong`}>Packaging & Labeling</div>

              <div className={styles.rowItemA}>
                <div className={styles.rowItemALabel}>Box Contents:</div>
                <div className={styles.rowItemAContent}>
                  <Select                  
                    options={[BoxContentsProvider.Boxem, BoxContentsProvider.Barcode2D]}
                    optionStates={boxContentStates}
                    defaultOption={BoxContentsProvider.Boxem}
                    disabled={createShipmentForm.shipmentType === TransportType.LTL || loadingMoveToStep1}
                    optionRender={option => {
                      switch (option) {
                        case BoxContentsProvider.Boxem:
                          return "Boxem Provided";

                        case BoxContentsProvider.Barcode2D:
                          return (
                            <div className={styles.rowItemA}>
                              2D Barcodes{(subscription && subscription.planCode.indexOf('professional') < 0) ? (
                                <Button
                                  type="primary"
                                  onClick={(e) => {
                                    e.stopPropagation(true);
                                    // open the upgrade plan page modal
                                    setChangeSubscriptionModalMode(ChangeSubscriptionModalMode.update);
                                  }}
                                  className={styles.upgradeButton}
                                  >
                                    Upgrade Plan
                                  </Button>
                              ) : (
                                <></>
                              )}
                          </div>
                        );
                      }
                    }}
                    selectionRender={option => {
                      switch (option) {
                        case BoxContentsProvider.Boxem:
                          return "Boxem Provided";

                        case BoxContentsProvider.Barcode2D:
                          if (createShipmentForm.shipmentType === TransportType.LTL) {
                            return (
                              <Tooltip title="LTL shipments are required to have 2D Barcodes for their box contents">
                                2D Barcodes
                              </Tooltip>
                            );
                          } else {
                            return "2D Barcodes";
                          }
                      }
                    }}
                    fullWidth
                    value={createShipmentForm.boxContentsProvider}
                    onChange={e => {
                      setCreateShipmentForm({...createShipmentForm, boxContentsProvider: e  
                    })}}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`${styles.col}`}> 
            <Flex direction="column" gap={2} className={`${styles.rightCol}`}>
              <Accordion
                title="Auto Pricing"
                defaultExpanded={false}
                onCollapseChange={(collapsed, ref) => {
                  if (collapsed) {
                    if (ref.current) {
                      ref.current.style.overflow = "hidden";
                    }
                    window.localStorage.setItem(LocalStorageKeys.autoPricingCollapsed, LocalStorageKeys.autoPricingCollapsed);
                  } else {
                    if (ref.current) {
                      ref.current.style.overflow = "visible";
                    }
                    window.localStorage.removeItem(LocalStorageKeys.autoPricingCollapsed);
                  }
                }}
              >
                <Flex direction="column" gap={2}>
                  <Switch
                    label="Auto-fill list price"
                    disabled={loadingMoveToStep1}
                    value={createShipmentForm.autoFillListPrice}
                    onChange={e => setCreateShipmentForm({...createShipmentForm, autoFillListPrice: e })}
                    caption="Enable to configure the list price default."
                  />
                  {createShipmentForm.autoFillListPrice && (
                    <div className={styles.inlineRow}>
                      <span style={{ minWidth: "9rem" }}>Match list price with</span>
                      <Select
                        value={createShipmentForm.autoFillListPriceFrom}
                        disabled={loadingMoveToStep1}
                        onChange={e => setCreateShipmentForm({...createShipmentForm, autoFillListPriceFrom: e })}
                        options={[AutoFillListPriceFrom.BUY_BOX,AutoFillListPriceFrom.HIGHEST_FBA_OFFER,AutoFillListPriceFrom.LOWEST_FBA_OFFER]}
                        optionRender={option => {
                          switch (option) {
                            case AutoFillListPriceFrom.BUY_BOX:
                              return "Buy Box";

                            case AutoFillListPriceFrom.HIGHEST_FBA_OFFER:
                              return "Highest FBA offer";

                            case AutoFillListPriceFrom.LOWEST_FBA_OFFER:
                              return "Lowest FBA offer";
                          }
                        }}
                      />
                      <span>and</span>
                      <Select
                        value={createShipmentForm.autoFillListPriceOperation}
                        disabled={loadingMoveToStep1}
                        onChange={e => setCreateShipmentForm({...createShipmentForm, autoFillListPriceOperation: e})}
                        options={[AutoFillListPriceOperation.INCREASE,AutoFillListPriceOperation.DECREASE]}
                        optionRender={option => {
                          switch (option) {
                            case AutoFillListPriceOperation.INCREASE:
                              return "Increase";

                            case AutoFillListPriceOperation.DECREASE:
                              return "Decrease";
                          }
                        }}
                      />
                      <span>by</span>
                      <InputNumber
                        showControls={false}
                        disabled={loadingMoveToStep1}
                        value={parseFloat(createShipmentForm.autoFillListPriceAmount)}
                        onChange={e => {
                          setCreateShipmentForm({...createShipmentForm, autoFillListPriceAmount: e.toString() })
                        }}
                        suffix={<span className={`weak`}>%</span>}
                      />
                    </div>
                  )}
                </Flex>
              </Accordion>
          
              <Accordion title="Auto-Print FNSKU" defaultExpanded={!!window.localStorage.getItem(LocalStorageKeys.autoPrintFNSKUExpanded)} onCollapseChange={collapsed => {
                if (collapsed) {
                  window.localStorage.removeItem(LocalStorageKeys.autoPrintFNSKUExpanded);
                
                } else {
                  window.localStorage.setItem(LocalStorageKeys.autoPrintFNSKUExpanded, LocalStorageKeys.autoPrintFNSKUExpanded);
                }
              }}>
                <Flex direction="column" gap={2}>
                  <Switch
                    label="After assigning to boxes"
                    disabled={loadingMoveToStep1}
                    value={createShipmentForm.printFNSKUAfterAssigningToBox}
                    onChange={v => {
                      createShipmentForm.printFNSKUAfterAddingToShipment = false; // unset this, they are mutually exclusive
                      createShipmentForm.printFNSKUAfterIncreaseQuantityInShipment = false;
                      createShipmentForm.printFNSKUAfterAssigningToBox = v;
                      if (!v) {
                        createShipmentForm.printFNSKUAfterIncreaseQuantityInBox = false;
                      }
                      setCreateShipmentForm({...createShipmentForm});
                    }}
                  />
                  <Switch
                    label="On quantity update"
                    disabled={loadingMoveToStep1 || !createShipmentForm.printFNSKUAfterAssigningToBox}
                    value={createShipmentForm.printFNSKUAfterIncreaseQuantityInBox}
                    onChange={v => {
                      createShipmentForm.printFNSKUAfterIncreaseQuantityInBox = v;
                      setCreateShipmentForm({...createShipmentForm});
                    }}
                  />
                </Flex>
              </Accordion>

              <Accordion title="Meta Data" defaultExpanded={!window.localStorage.getItem(LocalStorageKeys.metadataCollapsed)} onCollapseChange={collapsed => {
                if (collapsed) {
                  window.localStorage.setItem(LocalStorageKeys.metadataCollapsed, LocalStorageKeys.metadataCollapsed);
                } else {
                  window.localStorage.removeItem(LocalStorageKeys.metadataCollapsed);
                }
              }}>
                <div className={styles.inlineRow}>
                  <Switch
                    label="Buy Cost Input"
                    value={createShipmentForm.showBuyCostInput}
                    onChange={e => setCreateShipmentForm({...createShipmentForm, showBuyCostInput: e})}
                  />
                  <Switch
                    label="Supplier Input"
                    value={createShipmentForm.showSupplierInput}
                    onChange={e => setCreateShipmentForm({...createShipmentForm, showSupplierInput: e})}
                  />
                  <Switch
                    label="Date Purchased Input"
                    value={createShipmentForm.showDatePurchasedInput}
                    onChange={e => setCreateShipmentForm({...createShipmentForm, showDatePurchasedInput: e})}
                  />
                </div>
              </Accordion>
            </Flex>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            size="large"
            disabled={moveToStep1ButtonDisabled || loadingMoveToStep1}
            onClick={() => {
              if (showLTLWarningModal && createShipmentForm.shipmentType === TransportType.LTL) {
                setLTLWarningModalOpen(true);
                return;
              }
  
              if (true || showNonContiguous48StatesWarningModal) {
                const address = addressBook.find(a => a.id === createShipmentForm.fromAddressId);
                if (address && (address.state === "HI" || address.state === "AL")) {
                  setNonSupportedCarrierModalOpen(true);
                  return;
                }
              }

              if (show2DBarcodeModal) {
                setFirst2DBarcodeShipmentModalOpen(true);

              } else {
                handleCreateShipment();
              }
            }}
          >
            {loadingMoveToStep1 ? (
              <div className={styles.createShipLoaderContainer}>
                <LoadingSpinner size="1rem" />
              </div>
            ) : (
              <>
                <IconPlus />
                Create Shipment
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FbaCreatePage;
